'use client'

import React, { useState, useMemo, useRef, useEffect } from 'react'
import { Sources, DeltaStatic } from 'quill'
import ReactQuill, { Quill } from 'react-quill'
import { UnprivilegedEditor } from './interfaces/UnprivilegedEditor'
import { TextEditorProps } from './interfaces/TextEditorProps'
import 'react-quill/dist/quill.snow.css'
import styles from './TextEditor.module.scss'
import './fixedStyle.css'

const TextEditor = (props: TextEditorProps) => {
  const { onChange, defaultValue, processFile, rtl } = props
  const [value, setValue] = useState<DeltaStatic | string>('')
  const fileInput = useRef<HTMLInputElement>(null)
  // const [quillEditor, setQuillEditor] = useState<UnprivilegedEditor | null>(null)
  const quillRef = useRef<ReactQuill | null>(null)

  function handleUploadImage () {
    const quill = quillRef.current?.getEditor()
    if (quill) {
      fileInput.current?.click()
    }
  }

  const modules = useMemo(
    () => ({
      toolbar: props.disableEdits ? false : {
        container: [
          [{ header: [3, 4, false] }],
          ['bold', 'italic'],
          [{ list: 'bullet' }, { list: 'ordered'}],
          ['blockquote', 'link'],
          ['divider'],
          ['image'],
          [{ align: [] }],
          ['clean']
        ],
        handlers: {
          image: () => handleUploadImage()
        }
      }
    }),
    []
  )

  function handleTextChange (value: string, delta: DeltaStatic, source: Sources, editor: UnprivilegedEditor) {
    setValue(value)
    // setQuillEditor(editor)
    // console.log(JSON.stringify(editor.getContents()))
    if (onChange) onChange(editor.getContents())
  }

  const onChangeImage = () => {
    const files = fileInput.current?.files
    // Image upload function
    // console.log('quillEditor', quillEditor)
    const quill = quillRef.current?.getEditor()
    if (quill && files && processFile) processFile(files, quill)
    // if (processFile) processFile(files, quillEditor)
  }

  useEffect(() => {
    // Horizontal line
    const BlockEmbed = Quill.import('blots/block/embed')

    class DividerBlot extends BlockEmbed {
      alert?: false
    }

    DividerBlot.blotName = 'divider'
    DividerBlot.tagName = 'hr'
    DividerBlot.suppressWarning = true

    Quill.register(DividerBlot)

    const toolbar = document.querySelector('.ql-toolbar')
    const triggerEle = document.querySelector('.ql-image')

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) {
          toolbar?.classList.add('fixed-toolbar')
        } else {
          toolbar?.classList.remove('fixed-toolbar')
        }
      })
    }, { threshold: 0.1 })
    if (triggerEle) observer.observe(triggerEle)

    if (quillRef.current != null) {
      const quill = quillRef.current.getEditor()

      if (!props.disableEdits) {
        // Add a custom toolbar handler for the divider
        quill.getModule('toolbar').addHandler('divider', () => {
          const range = quill.getSelection()
          // @ts-expect-error Quill types don't include this custom embed signature.
          if (range) quill.insertEmbed(range.index, 'divider', true, Quill.sources.USER)
        })
      }


      const dividerButton = document.querySelector('.ql-divider')
      if (dividerButton) {
        dividerButton.innerHTML = '<svg viewBox="0 0 18 18"><line class="ql-stroke" x1="3" y1="9" x2="15" y2="9"></line></svg>'
      }
    }

    return () => {
      observer.disconnect()
    }
  }, [])

  return (
    <div className={`${styles.wrapper} ${props.disableEdits ? 'disableToolbar' : ''}`}>
      {!(props.disableEdits || false) &&
        <input
          type='file'
          style={{ display: 'none' }}
          ref={fileInput}
          accept='.png, .jpeg, .jpg, .webp'
          className='ql-image'
          onChange={onChangeImage}
        />
      }
      {typeof window !== 'undefined' && ReactQuill &&
        <div>
          <ReactQuill
            readOnly={props.disableEdits || false}
            ref={quillRef}
            modules={modules}
            theme='snow'
            value={value || defaultValue}
            // defaultValue={defaultValue || ''}
            onChange={handleTextChange}
            scrollingContainer={'html'}
            className={rtl ? 'rtl' : ''}
            formats={[
              'header',
              'bold',
              'italic',
              'list',
              'bullet',
              'blockquote',
              'link',
              'image',
              'divider'
            ]}
          />
        </div>
      }
    </div>
  )
}


export default TextEditor
